import { getJdiOSFile } from '@/util';

/**
 * 检查是否支持 webp 格式
 * @description 借助 canvas 并配合本地存储检查 webp 支持情况。
 *
 * @returns {boolean} 返回是否支持webp
 * @example
 *
 * const isSupport = getWebpSupport() // 返回 true 或者 false
 */

function getWebpSupport() {
  const WEBP_NAME = 'o2_webpSupport'; // 检查缓存

  const storage = localStorage;
  const isSupport = storage.getItem(WEBP_NAME);

  if (isSupport === 'true') {
    return true;
  } // 通过 canvas 来检查

  const support =
    document
      .createElement('canvas')
      .toDataURL('image/webp')
      .indexOf('data:image/webp') === 0; // 记录在本地

  storage.setItem(WEBP_NAME, support ? 'true' : 'false'); // 返回结果

  return support;
}

const webpSupport = getWebpSupport();
/**
 * 图片统一处理函数
 * @description 统一图片路径域名，以及裁剪图片宽高
 * @param {string} url - [必填]图片地址
 * @param {number} width - [必填]裁剪宽度
 * @param {number} [height] - 裁剪高度，默认为裁剪宽度大小
 *
 * @returns {string} 处理后的图片地址
 * @example
 *
 * const cover = 'http://img10.360buyimg.com/ling/jfs/t1/11801/14/12271/78918/5c8f620dE35b2ee1a/6ae92ca018000696.png'
 * const img = getImg(cover, 100, 50) // http://img10.360buyimg.com/ling/jfs/t1/11801/14/12271/78918/5c8f620dE35b2ee1a/6ae92ca018000696.png!cc_100x50.webp
 */

export default function getImg(url: string, width: number, height?: number) {
  if (!height) {
    height = width;
  }
  if (!url) return '';

  if (url.indexOf('data:image') === 0 && url.indexOf('base64') !== -1) {
    return url;
  } // 去除链接中空白字符

  url = url.replace(/\s+/g, ''); // 原始图片地址

  url = url.replace(/!.+$/, ''); // 统一协议头

  url = url.replace(/^(https?:)?\/\//i, 'http://'); // 拼接域名和业务名

  if (!/^http/i.test(url)) {
    url = 'http://img10.360buyimg.com/img/'.concat(url);
  }

  url = url.replace(/^(https?:)?\/\//i, '//');
  const IS_GIF = /\.gif/i.test(url);
  const IS_JFS = /jfs\//.test(url);
  const IS_CDN = /(m|img\d{1,2})\.360buyimg\.com/.test(url);
  const IS_IMG = /\.(jpg|jpeg|png|webp)/.test(url);
  const IS_WEBP = /\.webp/.test(url); // Gif 图不做后续处理

  if (IS_GIF) return url; // 非JFS 或 CDN 图片不处理

  if (!IS_JFS || !IS_CDN || !IS_IMG) {
    return url;
  } // 去除图片后缀的多余字符

  const rUrl = url?.match(/(\S*(jpg|jpeg|png|webp|gif))\s*/g);
  if (!rUrl) return url;
  url = rUrl[0]; // 设定宽高

  if (width) {
    url = url.replace(/(\/)(?:s\d+x\d+_)?(jfs\/)/, '$1s'.concat(`${width}`, 'x').concat(`${height}`, '_$2'));
    url += '!cc_'.concat(`${width}`, 'x').concat(`${height}`);
  } // 转webp

  if (webpSupport && !IS_WEBP) {
    url += '.webp';
  }

  const IS_JPG = /\.(jpg|jpeg)/.test(url); // 质量压缩

  if (IS_JPG) {
    url = url.replace(/(!q\d{1,2})?/, '');
  }

  return url;
}

/** 国际站 OSS 源站域名 */
export const JDI_INTL_OSS_HOST = 'jdi-intl.s3.cn-north-1.jdcloud-oss.com';

/** 国际站图片 CDN 域名 */
export const JD_IOS_CDN_HOST = 'jdios.jdindustry.com';

/** jdios OSS WebP 转码规则 */
export const JD_IOS_WEBP_OSS_PROCESS = 'img/fmt/webp';

/**
 * 将接口返回的 S3 图片域名统一替换为 jdios CDN 域名
 */
export function normalizeJdiosImageUrl(url?: string | null): string {
  if (!url?.trim()) {
    return '';
  }
  return url.trim().replace(JDI_INTL_OSS_HOST, JD_IOS_CDN_HOST);
}

/**
 * 将 jdios 图片 URL 转为 WebP 输出（x-oss-process=img/fmt/webp）
 * 浏览器不支持 WebP，或已是 WebP / GIF / base64 时返回规范化原图
 */
export function getJdiosWebpImageUrl(url?: string | null): string {
  const source = normalizeJdiosImageUrl(url);
  if (!source) {
    return '';
  }

  if (
    /^data:image\//i.test(source) ||
    /\.gif(?:\?|#|$)/i.test(source) ||
    /\.webp(?:\?|#|$)/i.test(source)
  ) {
    return source;
  }

  if (!webpSupport) {
    return source;
  }

  return getOssProcessedImageUrl(source, JD_IOS_WEBP_OSS_PROCESS, { normalizeHost: false });
}

/**
 * jdios 图片裁图并输出 WebP（x-oss-process=img/shb/{w}/{h}/fmt/webp）
 */
export function getJdiosShbWebpImageUrl(
  url?: string | null,
  width?: number,
  height?: number,
  options?: { normalizeHost?: boolean },
): string {
  if (!width || !height) {
    return getJdiosWebpImageUrl(url);
  }

  if (!webpSupport) {
    return getShbOssImageUrl(url, width, height, options);
  }

  return getOssProcessedImageUrl(url, `img/shb/${width}/${height}/fmt/webp`, options);
}

/**
 * 商品缩略图按展示尺寸 1x 裁切并转 WebP（订单列表/详情/结算等场景复用）
 * - jdios / 国际站 OSS：img/shb/{w}/{h}/fmt/webp
 * - 360buyimg JFS：getImg !cc_{w}x{h}.webp（保留接口原域名）
 * - 其他：仅 WebP
 */
export function getSkuCropImageUrl(
  url?: string | null,
  width?: number,
  height?: number,
): string {
  const source = url?.trim() || '';
  if (!source) {
    return '';
  }

  if (!width || !height) {
    return getJdiosWebpImageUrl(source);
  }

  if (source.includes(JD_IOS_CDN_HOST) || source.includes(JDI_INTL_OSS_HOST)) {
    return getJdiosShbWebpImageUrl(source, width, height);
  }

  if (/\.360buyimg\.com/i.test(source) && /jfs\//i.test(source)) {
    const normalized = source.replace(/^(https?:)?\/\//i, '//');
    return getImg(normalized, width, height);
  }

  return getJdiosWebpImageUrl(source);
}

/**
 * 首页图片统一 WebP 处理
 * - jdios / 国际站 OSS：img/fmt/webp
 * - 360buyimg JFS：沿用 getImg 的 .webp 规则
 */
export function getHomePageImageUrl(url?: string | null): string {
  const source = url?.trim() || '';
  if (!source) {
    return '';
  }

  if (/^data:image\//i.test(source) || /\.gif(?:\?|#|$)/i.test(source)) {
    return source;
  }

  const normalizedHost = normalizeJdiosImageUrl(source);
  if (
    normalizedHost.includes(JD_IOS_CDN_HOST) ||
    normalizedHost.includes(JDI_INTL_OSS_HOST) ||
    source.includes(JDI_INTL_OSS_HOST)
  ) {
    return getJdiosWebpImageUrl(source);
  }

  if (/\.360buyimg\.com/i.test(source) && /jfs\//i.test(source)) {
    const normalizedForGetImg = source.replace(/^(https?:)?\/\//i, '//');
    return getImg(normalizedForGetImg, 0);
  }

  return source;
}

/**
 * 为 jdios / 国际站 OSS 图片追加 x-oss-process 裁图参数
 * @param url 原始图片地址
 * @param ossProcess OSS 处理规则，如 img/shb/1280/360
 * @param options.normalizeHost 是否先做 S3 → jdios 域名替换，默认 true
 */
export function getOssProcessedImageUrl(
  url?: string | null,
  ossProcess?: string,
  options?: { normalizeHost?: boolean },
): string {
  if (!ossProcess?.trim()) {
    return options?.normalizeHost === false ? url?.trim() || '' : normalizeJdiosImageUrl(url);
  }

  const source =
    options?.normalizeHost === false ? url?.trim() || '' : normalizeJdiosImageUrl(url);
  if (!source) {
    return '';
  }

  return updatePathWithParams(source, { 'x-oss-process': ossProcess.trim() });
}

/**
 * 生成 img/shb/{width}/{height} 规则的 OSS 图片地址
 * @description 若用于全宽 Banner 等场景，建议传入「展示宽高 × 2」以适配 Retina
 */
export function getShbOssImageUrl(
  url?: string | null,
  width?: number,
  height?: number,
  options?: { normalizeHost?: boolean; retinaScale?: number },
): string {
  if (!width || !height) {
    return getOssProcessedImageUrl(url, '', options);
  }
  const scale = options?.retinaScale && options.retinaScale > 0 ? options.retinaScale : 1;
  const scaledWidth = Math.round(width * scale);
  const scaledHeight = Math.round(height * scale);
  return getOssProcessedImageUrl(url, `img/shb/${scaledWidth}/${scaledHeight}`, {
    normalizeHost: options?.normalizeHost,
  });
}

export function updatePathWithParams(url: string, params: { [key: string]: string }): string {
  // 检查输入参数的有效性
  if (typeof url !== 'string' || !url.trim()) {
      console.error('Invalid URL');
  }

  if (typeof params !== 'object' || params === null) {
     console.error('Invalid params object');
  }

  try {
      // 解析 URL
      const parsedUrl = new URL(url);
      const searchParams = new URLSearchParams(parsedUrl.search);

      // 更新 URL 中已有的参数
      for (const [key, value] of Object.entries(params)) {
          if (searchParams.has(key)) {
              searchParams.set(key, value);
          } else {
              searchParams.append(key, value);
          }
      }

      // 构建新的 URL
      parsedUrl.search = searchParams.toString();
      return parsedUrl.toString();
  } catch (error) {
      console.error('Error updating URL with params:', error);
      return url; // 返回原始 URL 以防止程序崩溃
  }
}

const EMPTY_PAGE_JDIOS_PATH = 'mubla1/224130122126489753.png';

/** 404 / 结算失败等空态插图（NotFound、OrderInfo 共用） */
export const EMPTY_PAGE_IMAGE_SIZE = 240;

export const EMPTY_PAGE_IMAGE_URL = getJdiosShbWebpImageUrl(
  getJdiOSFile(EMPTY_PAGE_JDIOS_PATH),
  EMPTY_PAGE_IMAGE_SIZE,
  EMPTY_PAGE_IMAGE_SIZE,
);
